import { ColumnDef } from "@components/spreadsheet/types";
import { AccrualImporterRow } from "../types";

export interface InvoiceGroupSlice {
    groupKey: string;
    headerRowIndex: number;
    lineRowIndices: number[];
    headerRow: AccrualImporterRow;
    lineRows: AccrualImporterRow[];
    warnings?: string[];
}

const LINE_ITEM_FIELDS = [
    "productOrService",
    "description",
    "classIds",
    "quantity",
    "unitPrice",
    "amount",
    "accrualLedger",
    "accrualAccountId",
] as const;

const RIGHT_PINNED_MONETARY_FIELDS = new Set([
    "quantity",
    "unitPrice",
    "amount",
    "totalAmount",
]);

function withRightPinnedMonetaryColumns(columns: ColumnDef[]): ColumnDef[] {
    return columns.map((column) =>
        RIGHT_PINNED_MONETARY_FIELDS.has(column.field)
            ? { ...column, pinned: "right" }
            : column,
    );
}

function reorderColumnsForPinning(columns: ColumnDef[]): ColumnDef[] {
    const leftPinned: ColumnDef[] = [];
    const center: ColumnDef[] = [];
    const rightPinned: ColumnDef[] = [];

    for (const column of columns) {
        if (column.pinned === "left") {
            leftPinned.push(column);
            continue;
        }
        if (column.pinned === "right") {
            rightPinned.push(column);
            continue;
        }
        center.push(column);
    }

    return [...leftPinned, ...center, ...rightPinned];
}

function applyGroupedSectionColumnLayout(columns: ColumnDef[]): ColumnDef[] {
    return reorderColumnsForPinning(withRightPinnedMonetaryColumns(columns));
}

function filterColumns(
    columns: ColumnDef[],
    fields: readonly string[],
): ColumnDef[] {
    const fieldSet = new Set<string>(fields);
    return columns.filter(
        (column) => column.field === "_rowNumber" || fieldSet.has(column.field),
    );
}

export function getLineItemColumns(columns: ColumnDef[]): ColumnDef[] {
    return applyGroupedSectionColumnLayout(
        filterColumns(columns, LINE_ITEM_FIELDS),
    );
}

export function getInvoiceGroupSlices(
    rows: AccrualImporterRow[],
): InvoiceGroupSlice[] {
    const slicesByGroupKey = new Map<string, InvoiceGroupSlice>();

    for (const [rowIndex, row] of rows.entries()) {
        const groupKey = row.extractionId;
        const existingSlice = slicesByGroupKey.get(groupKey);

        if (!existingSlice) {
            slicesByGroupKey.set(groupKey, {
                groupKey,
                headerRowIndex: rowIndex,
                lineRowIndices: [rowIndex],
                headerRow: row,
                lineRows: [row],
            });
            continue;
        }

        existingSlice.lineRowIndices.push(rowIndex);
        existingSlice.lineRows.push(row);
    }

    return Array.from(slicesByGroupKey.values());
}

export function removeInvoiceGroup({
    rows,
    groupKey,
}: {
    rows: AccrualImporterRow[];
    groupKey: string;
}): AccrualImporterRow[] {
    const nextRows = rows.filter((row) => row.extractionId !== groupKey);
    return nextRows.length > 0 ? nextRows : rows;
}
